import { RadioButton, RadioButtonGroup, Tile } from '@carbon/react';
import { format0 } from 'utils/number';
import { endSentence } from 'utils/strings';
import {
  clusterOf,
  survivingWellOf,
  type WellCandidate,
  type WellConsolidationAction,
  type WellConsolidationState,
} from './consolidation-utils';
import styles from './well-consolidation-survivors.module.scss';

/**
 * How a record reads in the survivor picker: enough to tell two near-identical rows
 * apart without leaving the tile.
 * @param {WellCandidate} well The record.
 * @return {string} Its label.
 */
const recordLabel = (well: WellCandidate): string => {
  const survey = well.survey
    ? ` · ${well.survey.source} · ${well.survey.stations} stations · MD ` +
      `${format0(well.survey.mdMinFt)}–${format0(well.survey.mdMaxFt)} ft`
    : '';
  return `${well.wellName} · UWI ${well.uwi}${survey}${
    well.isAnchor ? ' (viewed well)' : ''
  }`;
};

export interface WellConsolidationSurvivorsProps {
  state: WellConsolidationState;
  dispatch: (action: WellConsolidationAction) => void;
}

/**
 * Reconciliation result: a section below the consolidation table with one tile per
 * survivor, confirming which record actually survives the merge (the entity
 * tearsheet's result preview).
 *
 * The radio spans the survivor's whole cluster — its own included records plus those of
 * every name folded beneath it — because the merge is what the user is confirming, not
 * one name's membership. Picking a record from a folded name hands that name the
 * survivor role, which the reducer settles.
 * @param {WellConsolidationSurvivorsProps} props The model and its dispatch.
 * @return {JSX.Element | null} The section, or nothing when no survivor resolves.
 */
export function WellConsolidationSurvivors({
  state,
  dispatch,
}: WellConsolidationSurvivorsProps) {
  const tiles = state.survivorKeys
    .map((key) => ({
      key,
      survivor: survivingWellOf(state, key),
      cluster: clusterOf(state, key),
    }))
    .filter((tile) => tile.survivor !== null);

  if (tiles.length === 0) return null;

  return (
    <section className={styles.root} aria-label="Reconciliation result preview">
      <h4 className={styles.heading}>Reconciliation result</h4>
      {tiles.map(({ key, survivor, cluster }, index) => (
        <Tile key={key} className={styles.block}>
          <h5 className={styles.blockTitle}>{`Survivor ${index + 1}`}</h5>
          <p className={styles.subtitle}>
            {cluster.length > 1
              ? `${format0(cluster.length)} records merge — choose which one survives as ${endSentence(
                  survivor?.wellName ?? ''
                )}`
              : 'Kept as its own distinct record.'}
          </p>
          <RadioButtonGroup
            name={`reconcile-pin-${key}`}
            orientation="vertical"
            legendText="Surviving well"
            className={styles.radios}
            valueSelected={survivor?.rowId ?? ''}
            onChange={(value: string | number | undefined) =>
              value !== undefined &&
              dispatch({ type: 'setRepresentative', key, id: String(value) })
            }
          >
            {cluster.map((member) => (
              <RadioButton
                key={member.rowId}
                id={`reconcile-pin-${key}-${member.rowId}`}
                value={member.rowId}
                labelText={recordLabel(member)}
              />
            ))}
          </RadioButtonGroup>
        </Tile>
      ))}
    </section>
  );
}
